// OtpVerification.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

/**
 * OtpVerification
 * - Robust 6-digit OTP inputs (keyboard nav + paste support)
 * - Countdown timer and resend attempts (max 3)
 * - Verifies OTP via API and stores received real token + user info
 * - Redirects based on role
 *
 * Required env: REACT_APP_API_URL
 */
const OtpVerification = () => {
  const navigate = useNavigate();

  // initial checks
  const token = localStorage.getItem("tempToken");
  const roleFromStorage = localStorage.getItem("role");

  // If user already has a role/token, redirect accordingly
  useEffect(() => {
    if (!token) {
      navigate("/admin/sign_in", { replace: true });
      return;
    }

    if (roleFromStorage) {
      // if role exists we probably already verified; redirect
      if (roleFromStorage === "ADMIN") navigate("/admin/admin-work-pi", { replace: true });
     
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // OTP input state (6 single-char boxes)
  const OTP_LENGTH = 6;
  const [otpArr, setOtpArr] = useState(Array(OTP_LENGTH).fill(""));
  const inputsRef = useRef([]);

  // UI / control state
  const [timeLeft, setTimeLeft] = useState(30);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // isMounted guard to avoid state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // countdown timer
  useEffect(() => {
    setResendDisabled(timeLeft > 0);
    if (timeLeft <= 0) return undefined;

    const id = setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [timeLeft]);

  useEffect(() => {
    if (resendAttempts >= 3) {
      setResendDisabled(true);
      setTimeLeft(0);
    }
  }, [resendAttempts]);

  const focusInput = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
  };

  // single-digit change handler
  const handleChange = (value, idx) => {
    if (!/^\d*$/.test(value)) return; // only numbers
    const next = [...otpArr];
    next[idx] = value.slice(-1); // keep single char
    setOtpArr(next);
    if (value && idx < OTP_LENGTH - 1) focusInput(idx + 1);
  };

  // keyboard handling (backspace + arrow nav)
  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...otpArr];
      if (next[idx]) {
        next[idx] = "";
        setOtpArr(next);
        return;
      }
      if (idx > 0) {
        next[idx - 1] = "";
        setOtpArr(next);
        focusInput(idx - 1);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      focusInput(idx - 1);
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      e.preventDefault();
      focusInput(idx + 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerifyOtp();
    }
  };

  // paste support: accept up to 6 digits
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text") || "";
    const digits = pasted.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (digits.length === 0) return;
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setOtpArr(next);
    const focusIndex = digits.length >= OTP_LENGTH ? OTP_LENGTH - 1 : digits.length;
    focusInput(focusIndex);
  };

  const otpString = otpArr.join("");

 const [errorMessage, setErrorMessage] = useState("");

// Replace handleVerifyOtp with this:
const handleVerifyOtp = async () => {
  setErrorMessage("");

  if (otpString.length !== OTP_LENGTH || !/^\d+$/.test(otpString)) {
    toast.error("Enter a valid 6-digit OTP");
    return;
  }
  if (!token) {
    setErrorMessage("Missing temporary token. Please login again.");
    toast.error("Missing temporary token. Please login again.");
    navigate("/admin/sign_in", { replace: true });
    return;
  }

  setSubmitting(true);
  try {
    // console.log("Verifying OTP...", { apiUrl: process.env.REACT_APP_API_URL, otp: otpString, token });

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/verify-otp`,
      { token, otp: otpString },
      { timeout: 15000 }
    );

    // console.log("verify response:", res?.status, res?.data);

    // defensive checks
    if (!res || !res.data) {
      throw new Error("Invalid response from server");
    }

    if (res.data.success) {
      // guard: make sure data object exists
      const profile = res.data.data || {};

if (res.data.realToken) {
  localStorage.setItem("token", res.data.realToken);
}

const adminUser = {
  Role: profile.Role || "",
  Email: profile.Email || "",
  FullName: profile.FullName || "",
};

localStorage.setItem("adminUser", JSON.stringify(adminUser));

window.dispatchEvent(new Event("admin-auth-change"));

toast.success("OTP verified successfully!");


if (adminUser.Role === "ADMIN") {
  navigate("/admin/admin-work-pi");
} else {
  navigate("/admin/sign_in");
}
      
      
      
    } else {
      // server returned success:false
      const msg = res.data.message || "OTP verification failed";
      setErrorMessage(msg);
      toast.error(msg);
      // console.warn("OTP verify failed:", res.data);
    }
  } catch (err) {
    // show helpful messages for common cases
    // console.error("OTP verify error:", err);
    const serverMsg = err?.response?.data?.message;
    if (serverMsg) setErrorMessage(serverMsg);

    if (err?.message && err.message.includes("timeout")) {
      setErrorMessage("Request timed out. Please try again.");
      toast.error("Request timed out. Please try again.");
    } else if (err?.response && err.response.status === 401) {
     
      toast.error("Wrong OTP Entered, Please check entered OTP.");
     
    } 
  } finally {
    // ALWAYS reset submitting so UI won't get stuck
    setSubmitting(false);
  }
};

// Replace handleResendOtp with this:
const handleResendOtp = async () => {
  setErrorMessage("");
  if (!token) {
    setErrorMessage("Missing token. Please login again.");
    toast.error("Missing token. Please login again.");
    navigate("/admin/sign_in", { replace: true });
    return;
  }
  if (resendAttempts >= 3) {
    // setErrorMessage("Maximum resend attempts reached.");
    toast.error("Maximum resend attempts reached");
    return;
  }

  try {
    setResendDisabled(true);
    // console.log("Resend OTP request", { apiUrl: process.env.REACT_APP_API_URL, token });

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/resend-otp`,
      { token },
      { timeout: 12000 }
    );

    // console.log("resend response:", res?.status, res?.data);

    if (res?.data?.success) {
      toast.success("OTP resent. Check your email.");
      setResendAttempts((s) => s + 1);
      setTimeLeft(30);
    } else {
      const msg = res?.data?.message || "Failed to resend OTP";
      setErrorMessage(msg);
      toast.error(msg);
      // allow a short retry
      setTimeLeft(6);
    }
  } catch (err) {
    // console.error("Resend OTP error:", err);
    const serverMsg = err?.response?.data?.message;
    setErrorMessage(serverMsg || "Couldn't resend OTP right now");
    toast.error(serverMsg || "Couldn't resend OTP right now");
    setTimeLeft(6);
  } finally {
    // ensure UI consistent
    setResendDisabled(timeLeft > 0);
  }
};

  return (
  <main className="relative flex items-center justify-center min-h-screen px-4 py-8 overflow-hidden bg-slate-950">
    <ToastContainer
      position="top-center"
      autoClose={4500}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="dark"
      toastClassName="!rounded-xl !font-semibold"
    />

    {/* BACKGROUND EFFECTS */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.38),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(124,58,237,0.32),transparent_36%),radial-gradient(circle_at_center,rgba(14,165,233,0.16),transparent_46%)]"></div>

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>

      <div className="absolute w-64 h-64 rounded-full top-20 left-10 bg-cyan-400/20 blur-3xl"></div>
      <div className="absolute rounded-full bottom-20 right-10 w-72 h-72 bg-purple-500/20 blur-3xl"></div>
      <div className="absolute w-40 h-40 -translate-x-1/2 rounded-full top-1/2 left-1/2 bg-blue-500/10 blur-3xl"></div>
    </div>

    {/* MAIN CARD */}
    <section className="relative z-10 w-full max-w-5xl overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur-2xl">
      <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
        {/* LEFT PANEL */}
        <aside className="relative hidden p-8 overflow-hidden lg:flex">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.28),transparent_36%)]"></div>

          <div className="relative z-10 flex flex-col justify-between min-h-[560px]">
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Secure OTP Verification
              </span>

              <h1 className="mt-8 text-5xl font-black leading-tight tracking-tight text-white">
                Verify Your{" "}
                <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                  Admin Access
                </span>
              </h1>

              <p className="max-w-lg mt-5 text-base font-semibold leading-8 text-slate-300">
                We use email OTP verification to protect Practical Infosec admin
                operations, cohort data, course fee details, and platform
                controls.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 mt-10">
              <div className="p-5 border rounded-3xl bg-white/10 border-white/15">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 text-xl text-cyan-300 rounded-2xl bg-cyan-400/10">
                    <LockOutlined />
                  </div>

                  <div>
                    <p className="text-sm font-black text-white">
                      Two-Step Protection
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-300">
                      Password verification plus email OTP.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 border rounded-3xl bg-white/10 border-white/15">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 text-xl text-green-300 rounded-2xl bg-green-400/10">
                    ✓
                  </div>

                  <div>
                    <p className="text-sm font-black text-white">
                      Role Based Access
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-300">
                      Only authorized ADMIN users can continue.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-10 text-xs font-semibold text-slate-400">
              © {new Date().getFullYear()} Practical Infosec Pvt Ltd.
            </p>
          </div>
        </aside>

        {/* RIGHT OTP PANEL */}
        <section className="relative px-5 py-8 bg-white sm:px-8 lg:px-10">
          <div className="max-w-xl mx-auto">
            {/* HEADER */}
            <div className="mb-8 text-center">
              <div className="relative inline-flex items-center justify-center">
                <div className="absolute inset-0 rounded-[1.5rem] bg-blue-500/30 blur-2xl"></div>

                <div className="relative flex items-center justify-center w-20 h-20 text-3xl text-white shadow-2xl rounded-[1.5rem] bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-700 shadow-blue-500/30">
                  <LockOutlined />
                </div>
              </div>

              <span className="inline-flex items-center gap-2 px-4 py-2 mt-6 text-xs font-black tracking-widest text-blue-700 uppercase bg-blue-100 border border-blue-200 rounded-full">
                Admin Security Check
              </span>

              <h2 className="mt-4 text-4xl font-black tracking-tight text-slate-950">
                Enter OTP
              </h2>

              <p className="max-w-md mx-auto mt-3 text-sm font-semibold leading-6 text-slate-500">
                We&apos;ve sent a 6-digit verification code to your registered
                email address.
              </p>
            </div>

            {/* OTP CARD */}
            <div
              className="relative p-5 overflow-hidden border shadow-xl rounded-[2rem] border-slate-200 bg-slate-950"
              onPaste={handlePaste}
              aria-label="OTP input group"
              role="group"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.22),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(124,58,237,0.22),transparent_36%)]"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
                      Verification Code
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-300">
                      Type or paste the 6-digit OTP
                    </p>
                  </div>

                  <div className="px-3 py-1 text-xs font-black text-green-300 border rounded-full bg-green-400/10 border-green-400/20">
                    Secure
                  </div>
                </div>

                <label className="sr-only">Enter 6-digit OTP</label>

                <div className="flex justify-center gap-2 sm:gap-3">
                  {otpArr.map((val, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={val}
                      onChange={(e) => handleChange(e.target.value, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      className={`h-14 w-11 rounded-2xl border text-center text-xl font-black shadow-lg outline-none transition sm:h-16 sm:w-14 sm:text-2xl ${
                        val
                          ? "border-cyan-300 bg-cyan-50 text-blue-950 shadow-cyan-400/30 ring-4 ring-cyan-300/20"
                          : "border-white/10 bg-white/10 text-white focus:border-cyan-300 focus:bg-white focus:text-slate-950 focus:ring-4 focus:ring-cyan-300/20"
                      }`}
                      aria-label={`Digit ${idx + 1}`}
                    />
                  ))}
                </div>

                <div className="mt-5 text-center">
                  <p className="text-xs font-semibold text-slate-400">
                    OTP expires soon. Do not share this code with anyone.
                  </p>
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-[1fr_auto]">
              <button
                onClick={handleVerifyOtp}
                disabled={submitting || otpArr.join("").length !== 6}
                className="relative inline-flex items-center justify-center w-full gap-3 overflow-hidden px-6 py-4 text-base font-black text-white transition rounded-2xl bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 shadow-[0_0_35px_rgba(37,99,235,0.45)] hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(14,165,233,0.75)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
              >
                <span className="absolute inset-0 transition-transform duration-1000 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent hover:translate-x-full"></span>

                {submitting ? (
                  <>
                    <svg
                      className="relative z-10 w-5 h-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                    <span className="relative z-10">Verifying...</span>
                  </>
                ) : (
                  <span className="relative z-10">Verify OTP →</span>
                )}
              </button>

              <button
                onClick={handleResendOtp}
                disabled={resendDisabled || resendAttempts >= 3}
                className={`inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-black transition rounded-2xl shadow-lg ${
                  resendAttempts >= 3
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                    : resendDisabled
                    ? "bg-slate-100 text-slate-500 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700 hover:scale-105 shadow-red-500/20"
                }`}
              >
                <ReloadOutlined />
                Resend OTP
              </button>
            </div>

            {/* TIMER / RESEND STATUS */}
            <div className="p-5 mt-6 border rounded-3xl bg-slate-50 border-slate-200">
              {resendAttempts >= 3 ? (
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-red-600 bg-red-100 rounded-xl">
                    !
                  </div>

                  <div>
                    <p className="text-sm font-black text-slate-950">
                      Resend Attempts Exhausted
                    </p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                      You have used all 3 resend attempts. Please contact
                      support if you are unable to verify your account.
                    </p>
                  </div>
                </div>
              ) : resendDisabled ? (
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-black text-slate-950">
                      Resend available in
                    </p>

                    <span className="px-3 py-1 text-sm font-black text-blue-700 bg-blue-100 rounded-full">
                      {timeLeft}s
                    </span>
                  </div>

                  <div className="w-full h-2 mt-4 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full transition-all duration-500 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400"
                      style={{
                        width: `${((30 - timeLeft) / 30) * 100}%`,
                      }}
                    />
                  </div>

                  <p className="mt-3 text-xs font-semibold text-slate-500">
                    Attempt {resendAttempts} of 3
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-slate-950">
                      You can resend now
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-500">
                      Attempt {resendAttempts} of 3 used.
                    </p>
                  </div>

                  <span className="px-3 py-1 text-xs font-black text-green-700 bg-green-100 rounded-full">
                    Ready
                  </span>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="grid grid-cols-1 gap-3 mt-6 sm:grid-cols-3">
              <div className="p-4 text-center bg-white border rounded-2xl border-slate-200">
                <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
                  Step 1
                </p>
                <p className="mt-1 text-xs font-bold text-slate-600">
                  Login
                </p>
              </div>

              <div className="p-4 text-center border border-blue-200 rounded-2xl bg-blue-50">
                <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
                  Step 2
                </p>
                <p className="mt-1 text-xs font-bold text-slate-600">
                  OTP Verify
                </p>
              </div>

              <div className="p-4 text-center bg-white border rounded-2xl border-slate-200">
                <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
                  Step 3
                </p>
                <p className="mt-1 text-xs font-bold text-slate-600">
                  Admin Panel
                </p>
              </div>
            </div>

            <p className="mt-6 text-xs font-semibold text-center text-slate-400">
              Protected admin verification by Practical Infosec.
            </p>
          </div>
        </section>
      </div>
    </section>
  </main>
);
};

export default OtpVerification;
